import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Search } from 'lucide-react'
import { ActionButton, Badge, Button, Card } from '@/components/ui'
import { PedagogyBackLink } from '@/components/pedagogy/PedagogyBackLink'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import {
  assignPedagogySpecialist,
  listPedagogySpecialistPartnerCoverage,
  listPedagogySpecialists,
  transferPedagogySpecialist,
  unassignPedagogySpecialist,
  updatePedagogySpecialistAssignmentStart,
} from '@/services/pedagogySpecialistService'
import type {
  PedagogySpecialistListItem,
  PedagogySpecialistPartnerCoverageRow,
} from '@/types'
import { formatPartnerDisplay } from '@/lib/utils'
import { assignmentCloseActionLabel, describeAssignmentClose } from '@/lib/assignmentBoundary'

type CoverageFilter = 'all' | 'assigned' | 'unassigned' | 'attention'

type CoverageGroup = {
  key: string
  specialistName: string
  rows: PedagogySpecialistPartnerCoverageRow[]
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('ru-RU')
}

function getCoverageFilter(searchParams: URLSearchParams): CoverageFilter {
  if (searchParams.get('attention') === 'inactive-studios') return 'attention'
  const mode = searchParams.get('mode')
  if (mode === 'assigned' || mode === 'unassigned') return mode
  return 'all'
}

function getSpecialistLabel(row: PedagogySpecialistPartnerCoverageRow): string {
  if (!row.specialist_user_id) return 'Без специалиста'
  return row.specialist_name || `#${row.specialist_user_id.slice(0, 8)}`
}

function sortCoverageRows(a: PedagogySpecialistPartnerCoverageRow, b: PedagogySpecialistPartnerCoverageRow): number {
  if (a.assignment_needs_attention !== b.assignment_needs_attention) {
    return a.assignment_needs_attention ? -1 : 1
  }
  return getSpecialistLabel(a).localeCompare(getSpecialistLabel(b), 'ru')
    || a.partner_number - b.partner_number
}

function groupCoverageRows(rows: PedagogySpecialistPartnerCoverageRow[]): CoverageGroup[] {
  const groups = new Map<string, CoverageGroup>()
  for (const row of rows) {
    const key = row.specialist_user_id || 'unassigned'
    const existing = groups.get(key)
    if (existing) {
      existing.rows.push(row)
    } else {
      groups.set(key, {
        key,
        specialistName: getSpecialistLabel(row),
        rows: [row],
      })
    }
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'unassigned') return -1
    if (b.key === 'unassigned') return 1
    const aAttention = a.rows.some(row => row.assignment_needs_attention)
    const bAttention = b.rows.some(row => row.assignment_needs_attention)
    if (aAttention !== bAttention) return aAttention ? -1 : 1
    return a.specialistName.localeCompare(b.specialistName, 'ru')
  })
}

export function PedagogySpecialistsCoveragePage() {
  const { canManagePedagogy, loading: accessLoading } = usePedagogyAccess()
  const [searchParams, setSearchParams] = useSearchParams()

  const [rows, setRows] = useState<PedagogySpecialistPartnerCoverageRow[]>([])
  const [specialists, setSpecialists] = useState<PedagogySpecialistListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [focusSpecialist, setFocusSpecialist] = useState<string>('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [chosenSpecialist, setChosenSpecialist] = useState<string>('')
  const [assignedFrom, setAssignedFrom] = useState(() => formatLocalDate(new Date()))
  const [busy, setBusy] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmStep, setConfirmStep] = useState<'review' | 'final'>('review')
  const [closeAssignmentRow, setCloseAssignmentRow] = useState<PedagogySpecialistPartnerCoverageRow | null>(null)
  const [closeAssignmentDate, setCloseAssignmentDate] = useState(() => formatLocalDate(new Date()))
  const [closeAssignmentBusy, setCloseAssignmentBusy] = useState(false)
  const coverageFilter = getCoverageFilter(searchParams)

  const reload = () => {
    setLoading(true)
    Promise.all([listPedagogySpecialistPartnerCoverage(), listPedagogySpecialists()])
      .then(([coverageRows, specialistRows]) => {
        setRows(coverageRows)
        setSpecialists(specialistRows)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [])

  const searchFilteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const num = q ? Number(q) : null
    return rows.filter(row => {
      if (!q) return true
      if (num !== null && !Number.isNaN(num) && row.partner_number === num) return true
      if (row.specialist_name && row.specialist_name.toLowerCase().includes(q)) return true
      if (row.persons_fio && row.persons_fio.toLowerCase().includes(q)) return true
      if (row.cities_text && row.cities_text.toLowerCase().includes(q)) return true
      return false
    })
  }, [rows, search])

  const stats = useMemo(() => {
    const activeRows = rows.filter(row => row.has_active_studios !== false)
    const without = activeRows.filter(row => !row.specialist_user_id).length
    const withSpecialist = activeRows.length - without
    const attention = rows.filter(row => row.assignment_needs_attention).length
    return { total: activeRows.length, with: withSpecialist, without, attention }
  }, [rows])

  const visibleRows = useMemo(() => {
    return searchFilteredRows
      .filter(row => {
        if (focusSpecialist !== 'all' && row.specialist_user_id !== focusSpecialist) return false
        if (coverageFilter === 'attention') return row.assignment_needs_attention
        if (coverageFilter === 'assigned') return Boolean(row.specialist_user_id) && !row.assignment_needs_attention
        if (coverageFilter === 'unassigned') return row.has_active_studios !== false && !row.specialist_user_id
        return row.has_active_studios !== false || row.assignment_needs_attention
      })
      .sort(sortCoverageRows)
  }, [coverageFilter, focusSpecialist, searchFilteredRows])

  const coverageGroups = useMemo(
    () => groupCoverageRows(visibleRows),
    [visibleRows]
  )

  const selectedVisibleCount = useMemo(
    () => visibleRows.filter(row => selected.has(row.partner_number)).length,
    [selected, visibleRows]
  )

  const toggleSelect = (partnerNumber: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(partnerNumber)) next.delete(partnerNumber)
      else next.add(partnerNumber)
      return next
    })
  }

  const selectedRows = useMemo(
    () => rows.filter(row => selected.has(row.partner_number) && !row.assignment_needs_attention),
    [rows, selected]
  )

  const transferCount = useMemo(
    () => selectedRows.filter(row => row.specialist_user_id && row.specialist_user_id !== chosenSpecialist).length,
    [selectedRows, chosenSpecialist]
  )

  const chosenSpecialistName = useMemo(
    () => specialists.find(item => item.specialist_user_id === chosenSpecialist)?.specialist_name
      || (chosenSpecialist ? `#${chosenSpecialist.slice(0, 8)}` : ''),
    [specialists, chosenSpecialist]
  )

  const diff = useMemo(() => {
    const newAssign: PedagogySpecialistPartnerCoverageRow[] = []
    const transfer: PedagogySpecialistPartnerCoverageRow[] = []
    const dateChange: PedagogySpecialistPartnerCoverageRow[] = []
    const noChange: PedagogySpecialistPartnerCoverageRow[] = []
    for (const row of selectedRows) {
      if (!row.specialist_user_id) newAssign.push(row)
      else if (row.specialist_user_id !== chosenSpecialist) transfer.push(row)
      else if (row.assigned_from !== assignedFrom) dateChange.push(row)
      else noChange.push(row)
    }
    return { newAssign, transfer, dateChange, noChange }
  }, [selectedRows, chosenSpecialist, assignedFrom])

  const effectiveCount = diff.newAssign.length + diff.transfer.length + diff.dateChange.length

  const setCoverageFilter = (value: CoverageFilter) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'attention') next.set('attention', 'inactive-studios')
    else next.delete('attention')
    if (value === 'assigned' || value === 'unassigned') next.set('mode', value)
    else next.delete('mode')
    setSearchParams(next, { replace: true })
    setSelected(new Set())
  }

  const openCloseAssignment = (row: PedagogySpecialistPartnerCoverageRow) => {
    if (!row.assignment_id) return
    setActionError(null)
    setCloseAssignmentRow(row)
    setCloseAssignmentDate(row.suggested_unassigned_at || formatLocalDate(new Date()))
  }

  const closeAssignmentCloseOutcome = describeAssignmentClose(
    closeAssignmentDate,
    closeAssignmentRow?.assigned_from
  )

  const handleCloseAssignment = async () => {
    if (!closeAssignmentRow?.assignment_id || !closeAssignmentDate) return

    const outcome = describeAssignmentClose(closeAssignmentDate, closeAssignmentRow.assigned_from)
    if (outcome.removed && !window.confirm(
      'Дата не позже начала назначения, поэтому связь будет УДАЛЕНА целиком, а не закрыта. '
      + 'Восстановить её из этого экрана будет нельзя. Продолжить?'
    )) return

    setCloseAssignmentBusy(true)
    setActionError(null)
    try {
      await unassignPedagogySpecialist({
        assignmentId: closeAssignmentRow.assignment_id,
        unassignedAt: closeAssignmentDate,
        note: closeAssignmentRow.assignment_needs_attention
          ? 'Связь завершена из контроля назначений без активных студий'
          : 'Назначение снято из контроля назначений специалистов по педагогам',
      })
      setCloseAssignmentRow(null)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setCloseAssignmentBusy(false)
    }
  }

  const handleApply = async () => {
    if (!chosenSpecialist || selected.size === 0) return
    setBusy(true)
    setActionError(null)
    try {
      for (const row of selectedRows) {
        if (row.specialist_user_id === chosenSpecialist) {
          if (row.assignment_id && row.assigned_from !== assignedFrom) {
            await updatePedagogySpecialistAssignmentStart({
              assignmentId: row.assignment_id,
              assignedFrom,
            })
          }
          continue
        }
        if (row.specialist_user_id) {
          await transferPedagogySpecialist({
            partnerNumber: row.partner_number,
            newSpecialistUserId: chosenSpecialist,
            effectiveFrom: assignedFrom,
          })
        } else {
          await assignPedagogySpecialist({
            specialistUserId: chosenSpecialist,
            partnerNumber: row.partner_number,
            assignedFrom,
          })
        }
      }
      setSelected(new Set())
      setConfirmOpen(false)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!accessLoading && !canManagePedagogy) {
    return (
      <Card>
        <div className="text-sm text-muted-foreground">
          Эта страница доступна только руководителю педагогов и пользователям УК с уровнем доступа `advanced`.
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <PedagogyBackLink to={pedagogyPaths.department} label="К своду" />

        <div>
          <h1 className="text-2xl font-bold tracking-tight">Назначения франчайзи</h1>
          <p className="mt-1 text-sm text-muted-foreground">Распределение франчайзи между специалистами по педагогам.</p>
        </div>
      </div>

      <Card className="border border-border shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_240px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Поиск: № франчайзи, ФИО, город или специалист"
              className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
            />
          </div>
          <select
            value={focusSpecialist}
            onChange={event => setFocusSpecialist(event.target.value)}
            className="input h-10 w-full px-3 py-1 text-sm"
          >
            <option value="all">Все специалисты</option>
            {specialists.map(item => (
              <option key={item.specialist_user_id} value={item.specialist_user_id}>
                {item.specialist_name || `#${item.specialist_user_id.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
          <FilterButton active={coverageFilter === 'all'} onClick={() => setCoverageFilter('all')}>
            Все · {stats.total + stats.attention}
          </FilterButton>
          <FilterButton active={coverageFilter === 'assigned'} onClick={() => setCoverageFilter('assigned')}>
            Назначенные · {stats.with}
          </FilterButton>
          <FilterButton active={coverageFilter === 'unassigned'} onClick={() => setCoverageFilter('unassigned')}>
            Без специалиста · {stats.without}
          </FilterButton>
          <FilterButton active={coverageFilter === 'attention'} onClick={() => setCoverageFilter('attention')}>
            Требуют проверки · {stats.attention}
          </FilterButton>
        </div>
      </Card>

      {(selectedVisibleCount > 0 || focusSpecialist !== 'all') && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedVisibleCount > 0 && (
            <Badge text={`Выбрано на экране: ${selectedVisibleCount}`} variant="success" className="text-xs" />
          )}
          {focusSpecialist !== 'all' && (
            <Badge
              text={`Специалист: ${specialists.find(item => item.specialist_user_id === focusSpecialist)?.specialist_name || 'специалист'}`}
              variant="outline"
              className="text-xs"
            />
          )}
        </div>
      )}

      {selected.size > 0 && (
        <Card className="border border-primary/30 bg-primary/5">
          <div className="space-y-3">
            <div className="text-sm font-semibold">Назначить {selected.size} франчайзи</div>
            <div className="flex flex-wrap gap-2">
              <select
                value={chosenSpecialist}
                onChange={event => setChosenSpecialist(event.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 border border-border rounded-md bg-background text-sm"
              >
                <option value="">Выберите специалиста...</option>
                {specialists.map(item => (
                  <option key={item.specialist_user_id} value={item.specialist_user_id}>
                    {(item.specialist_name || `#${item.specialist_user_id.slice(0, 8)}`)} · {item.partners_count} франч.
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={assignedFrom}
                onChange={event => setAssignedFrom(event.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-background text-sm"
              />
              <Button
                size="sm"
                disabled={!chosenSpecialist || busy}
                onClick={() => { setActionError(null); setConfirmStep('review'); setConfirmOpen(true) }}
              >
                Применить
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelected(new Set())}
              >
                Очистить выбор
              </Button>
            </div>
            {transferCount > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle size={12} />
                {transferCount} франчайзи будут переданы от текущего специалиста; предыдущий период закроется {assignedFrom} − 1 день.
              </div>
            )}
            {actionError && (
              <div className="text-xs text-destructive">Ошибка: {actionError}</div>
            )}
          </div>
        </Card>
      )}

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка...</div></Card>}
      {error && <Card><div className="text-sm text-destructive">Ошибка: {error}</div></Card>}

      {!loading && !error && visibleRows.length > 0 && (
        <CoverageTable
          groups={coverageGroups}
          selected={selected}
          onToggleSelect={toggleSelect}
          onCloseAssignment={openCloseAssignment}
        />
      )}

      {!loading && !error && visibleRows.length === 0 && (
        <Card><div className="text-sm text-muted-foreground">Ничего не найдено.</div></Card>
      )}

      {closeAssignmentRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => { if (!closeAssignmentBusy) setCloseAssignmentRow(null) }}
        >
          <div
            className="w-full max-w-lg rounded-card bg-card shadow-lg"
            onClick={event => event.stopPropagation()}
          >
            <div className="border-b border-border px-4 py-3">
              <div className="text-sm font-semibold">Снять назначение специалиста</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {formatPartnerDisplay(closeAssignmentRow.partner_number, closeAssignmentRow.persons_fio, closeAssignmentRow.cities_text)}
              </div>
            </div>
            <div className="space-y-4 px-4 py-4">
              {closeAssignmentRow.assignment_needs_attention ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-medium text-amber-800">Нет активных студий</div>
                  <div className="mt-1 text-xs text-muted-foreground">Связь со специалистом осталась и продолжает считаться действующей.</div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-medium text-amber-800">Франчайзи останется без специалиста</div>
                  <div className="mt-1 text-xs text-muted-foreground">Новый специалист автоматически назначен не будет. При необходимости назначьте его отдельным действием.</div>
                </div>
              )}

              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Первый день без назначения</span>
                <input
                  type="date"
                  value={closeAssignmentDate}
                  onChange={event => setCloseAssignmentDate(event.target.value)}
                  className="input mt-1 h-9 w-full px-3 py-1 text-sm"
                />
                {/*
                  Эхо введённой даты. Пояснения ниже привязаны к СЕРВЕРНОЙ
                  подсказке и при смене даты не меняются ни на символ — то есть
                  подтверждают исходную дату, даже когда оператор ввёл другую.
                  Это боевой класс бага контура ТУ (`20260807120000`).
                */}
                <span className="mt-1 block text-xs font-medium text-foreground">
                  {closeAssignmentCloseOutcome.removed
                    ? 'назначение будет удалено полностью'
                    : closeAssignmentCloseOutcome.lastActiveDay
                      ? `числится по ${formatDate(closeAssignmentCloseOutcome.lastActiveDay)} включительно`
                      : 'укажите дату'}
                </span>
              </label>

              {closeAssignmentRow.has_active_studios ? (
                <div className="text-xs text-muted-foreground">Выбранная дата — первый день, когда специалист уже не отвечает за франчайзи.</div>
              ) : closeAssignmentRow.suggested_unassigned_at ? (
                <div className="text-xs text-muted-foreground">
                  Предлагаемая дата рассчитана по последнему дню активной студии
                  {closeAssignmentRow.last_active_studio_on ? ` (${formatDate(closeAssignmentRow.last_active_studio_on)})` : ''}.
                  Назначение будет действовать по предыдущий день включительно.
                </div>
              ) : closeAssignmentRow.has_future_studios ? (
                <div className="text-xs text-amber-800">
                  У франчайзи есть студия с будущей датой начала. Автоматическую дату завершения не предлагаем — проверьте, должна ли связь сохраняться.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Надёжную историческую дату определить не удалось. Проверьте дату перед завершением связи.
                </div>
              )}

              {closeAssignmentCloseOutcome.removed && closeAssignmentRow.assigned_from && (
                <div className="text-xs text-amber-800">
                  Дата не позже начала назначения ({formatDate(closeAssignmentRow.assigned_from)}), поэтому эта связь будет удалена как не имевшая рабочего интервала.
                </div>
              )}

              {actionError && (
                <div className="text-xs text-error">{actionError}</div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <Button size="sm" variant="outline" disabled={closeAssignmentBusy} onClick={() => setCloseAssignmentRow(null)}>
                Отмена
              </Button>
              <Button size="sm" disabled={closeAssignmentBusy || !closeAssignmentDate} onClick={() => void handleCloseAssignment()}>
                {closeAssignmentBusy
                  ? 'Завершаем…'
                  : assignmentCloseActionLabel(closeAssignmentDate, formatLocalDate(new Date()))}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => { if (!busy) setConfirmOpen(false) }}
        >
          <div
            className="bg-card rounded-card shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={event => event.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border">
              <div className="text-sm font-semibold">Подтверждение назначения</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Назначить специалиста <span className="text-foreground font-medium">{chosenSpecialistName}</span>
                {' '}с <span className="text-foreground font-medium">{assignedFrom}</span>.
                Выбрано {selected.size}, изменений: {effectiveCount}.
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
              {effectiveCount === 0 && (
                <div className="text-muted-foreground">
                  Все выбранные франчайзи уже назначены этому специалисту с указанной даты — изменений не будет.
                </div>
              )}

              {diff.newAssign.length > 0 && (
                <DiffSection title={`Новые назначения · ${diff.newAssign.length}`} accent="success">
                  {diff.newAssign.map(row => (
                    <DiffRow key={row.partner_number}>
                      <PartnerLabel row={row} />
                      <span className="text-xs text-muted-foreground">
                        ранее без специалиста → <span className="text-foreground">{chosenSpecialistName}</span> с {assignedFrom}
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.transfer.length > 0 && (
                <DiffSection title={`Передача от другого специалиста · ${diff.transfer.length}`} accent="warning">
                  <div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
                    <AlertTriangle size={11} />
                    Предыдущее назначение закроется датой {assignedFrom} − 1 день
                  </div>
                  {diff.transfer.map(row => (
                    <DiffRow key={row.partner_number}>
                      <PartnerLabel row={row} />
                      <span className="text-xs text-muted-foreground">
                        от <span className="text-foreground">{row.specialist_name}</span>
                        {row.assigned_from && <> (с {row.assigned_from})</>}
                        {' '}→ <span className="text-foreground">{chosenSpecialistName}</span> с {assignedFrom}
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.dateChange.length > 0 && (
                <DiffSection title={`Изменение даты начала · ${diff.dateChange.length}`} accent="neutral">
                  {diff.dateChange.map(row => (
                    <DiffRow key={row.partner_number}>
                      <PartnerLabel row={row} />
                      <span className="text-xs text-muted-foreground">
                        тот же специалист: дата {row.assigned_from ?? '—'} → <span className="text-foreground">{assignedFrom}</span>
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.noChange.length > 0 && (
                <DiffSection title={`Без изменений · ${diff.noChange.length}`} accent="muted">
                  {diff.noChange.map(row => (
                    <DiffRow key={row.partner_number}>
                      <PartnerLabel row={row} />
                      <span className="text-xs text-muted-foreground">
                        уже у {chosenSpecialistName} с {row.assigned_from ?? '—'} — будет пропущен
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {actionError && (
                <div className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle size={12} /> {actionError}
                </div>
              )}
            </div>

            {confirmStep === 'final' && (
              <div className="px-4 py-3 border-t border-border bg-error/5">
                <div className="text-sm font-semibold text-error flex items-center gap-2">
                  <AlertTriangle size={14} />
                  Окончательное подтверждение
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Будет применено {effectiveCount} изменени{effectiveCount === 1 ? 'е' : 'й'}
                  {diff.transfer.length > 0 && <>, в т.ч. передача {diff.transfer.length} франчайзи от другого специалиста</>}
                  . Отмена потребует ручных шагов.
                </div>
              </div>
            )}

            <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-end gap-2">
              {confirmStep === 'review' ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setConfirmOpen(false)}
                  >
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy || effectiveCount === 0}
                    onClick={() => setConfirmStep('final')}
                  >
                    Подтвердить ({effectiveCount})
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setConfirmStep('review')}
                  >
                    Назад
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || effectiveCount === 0}
                    onClick={handleApply}
                  >
                    {busy ? 'Применяем…' : 'Да, применить'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}) {
  const activeClass = 'border-primary bg-primary text-primary-foreground shadow-sm'
  const idleClass = 'border-border bg-card text-foreground hover:bg-accent'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold leading-none transition ${active ? activeClass : idleClass}`}
    >
      {children}
    </button>
  )
}

function CoverageTable({
  groups,
  selected,
  onToggleSelect,
  onCloseAssignment,
}: {
  groups: CoverageGroup[]
  selected: Set<number>
  onToggleSelect: (partnerNumber: number) => void
  onCloseAssignment: (row: PedagogySpecialistPartnerCoverageRow) => void
}) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="border-b border-border px-4 py-2">
        <div className="text-sm font-semibold leading-tight">Реестр назначений</div>
        <div className="mt-0.5 text-xs leading-snug text-muted-foreground">Одна строка — один франчайзи. Специалист сгруппирован слева, действия остаются в строке.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-separate border-spacing-0 text-[13px] leading-snug">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="w-[210px] border-b border-border px-3 py-2 text-left font-semibold">Специалист</th>
              <th className="w-10 border-b border-border px-2 py-2 text-left font-semibold"></th>
              <th className="border-b border-border px-3 py-2 text-left font-semibold">Франчайзи</th>
              <th className="w-[180px] border-b border-border px-3 py-2 text-left font-semibold">Контур</th>
              <th className="w-[170px] border-b border-border px-3 py-2 text-left font-semibold">Период</th>
              <th className="w-[190px] border-b border-border px-3 py-2 text-left font-semibold">Статус</th>
              <th className="w-[132px] border-b border-border px-3 py-2 text-right font-semibold">Действие</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(group => {
              const groupStudios = group.rows.reduce((sum, row) => sum + row.studio_count, 0)
              const groupAttention = group.rows.filter(row => row.assignment_needs_attention).length
              return group.rows.map((row, index) => {
                const isSelected = selected.has(row.partner_number)
                const canBulkAssign = !row.assignment_needs_attention
                return (
                  <tr
                    key={row.partner_number}
                    className={`border-b border-border/70 ${isSelected ? 'bg-primary/5' : 'bg-card'}`}
                  >
                    {index === 0 && (
                      <td rowSpan={group.rows.length} className="border-b border-border/70 px-3 py-2 align-top">
                        <div className="font-medium leading-snug">{group.specialistName}</div>
                        <div className="mt-1 text-xs leading-snug text-muted-foreground">
                          {group.rows.length} франч. · {groupStudios} студ.
                        </div>
                        {groupAttention > 0 && (
                          <div className="mt-1.5">
                            <Badge text={`Проверить: ${groupAttention}`} variant="warning" className="text-[11px]" />
                          </div>
                        )}
                      </td>
                    )}
                    <td className="border-b border-border/70 px-2 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(row.partner_number)}
                        className="mt-0.5 h-4 w-4"
                        disabled={!canBulkAssign}
                        title={canBulkAssign ? undefined : 'Сначала завершите оставшуюся связь'}
                      />
                    </td>
                    <td className="border-b border-border/70 px-3 py-2 align-top">
                      <div className="font-medium leading-snug">
                        {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                      </div>
                      {row.cities_text && (
                        <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{row.cities_text}</div>
                      )}
                    </td>
                    <td className="border-b border-border/70 px-3 py-2 align-top text-xs leading-snug">
                      <div className="font-medium text-foreground">{row.studio_count} студий</div>
                      {row.has_future_studios && (
                        <div className="mt-0.5 text-amber-800">есть будущие</div>
                      )}
                    </td>
                    <td className="border-b border-border/70 px-3 py-2 align-top text-xs leading-snug text-muted-foreground">
                      {row.assigned_from ? <div>с {formatDate(row.assigned_from)}</div> : <div>не назначен</div>}
                      {row.assigned_to && <div>по {formatDate(row.assigned_to)}</div>}
                      {row.last_active_studio_on && (
                        <div className="mt-0.5">студии до {formatDate(row.last_active_studio_on)}</div>
                      )}
                    </td>
                    <td className="border-b border-border/70 px-3 py-2 align-top">
                      {row.assignment_needs_attention ? (
                        <Badge text="Нет активных студий" variant="warning" className="whitespace-nowrap text-[11px]" />
                      ) : row.specialist_user_id ? (
                        <Badge text="назначен" variant="success" className="whitespace-nowrap text-[11px]" />
                      ) : (
                        <Badge text="без специалиста" variant="warning" className="whitespace-nowrap text-[11px]" />
                      )}
                    </td>
                    <td className="border-b border-border/70 px-3 py-2 text-right align-top">
                      {row.assignment_id ? (
                        <ActionButton onClick={() => onCloseAssignment(row)}>
                          {row.assignment_needs_attention ? 'Завершить' : 'Снять'}
                        </ActionButton>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                )
              })
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

interface DiffSectionProps {
  title: string
  accent: 'success' | 'warning' | 'neutral' | 'muted'
  children: React.ReactNode
}

function DiffSection({ title, accent, children }: DiffSectionProps) {
  const accentClasses = {
    success: 'border-success/40 bg-success/5',
    warning: 'border-primary/40 bg-primary/5',
    neutral: 'border-border bg-muted/30',
    muted: 'border-border/60 bg-muted/10',
  }[accent]
  const titleClasses = {
    success: 'text-success',
    warning: 'text-primary',
    neutral: 'text-foreground',
    muted: 'text-muted-foreground',
  }[accent]
  return (
    <div className={`rounded-md border ${accentClasses} p-2.5`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${titleClasses}`}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function DiffRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {children}
    </div>
  )
}

function PartnerLabel({ row }: { row: PedagogySpecialistPartnerCoverageRow }) {
  return (
    <span className="text-sm font-medium">
      {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
    </span>
  )
}
